import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Generic undo/redo state hook.
 *
 * Maintains a past/present/future stack. Push to past on every set(),
 * undo/redo move state between stacks. Capped at maxHistory entries.
 *
 * Keyboard bindings: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z (redo).
 *
 * @param {*} initialState
 * @param {number} maxHistory — max past entries to keep (default 50)
 * @returns {{ state, set, undo, redo, canUndo, canRedo, reset }}
 */
export function useUndoRedo(initialState, maxHistory = 50) {
  const [history, setHistory] = useState({
    past: [],
    present: initialState,
    future: [],
  });
  const historyRef = useRef(history);
  historyRef.current = history;

  const set = useCallback((nextStateOrFn) => {
    setHistory((prev) => {
      const next = typeof nextStateOrFn === 'function'
        ? nextStateOrFn(prev.present)
        : nextStateOrFn;
      // No-op if unchanged (reference equality)
      if (next === prev.present) return prev;
      const past = [...prev.past, prev.present];
      // Trim past if it exceeds maxHistory
      const trimmed = past.length > maxHistory ? past.slice(past.length - maxHistory) : past;
      return { past: trimmed, present: next, future: [] };
    });
  }, [maxHistory]);

  const undo = useCallback(() => {
    setHistory((prev) => {
      if (prev.past.length === 0) return prev;
      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, -1);
      return {
        past: newPast,
        present: previous,
        future: [prev.present, ...prev.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory((prev) => {
      if (prev.future.length === 0) return prev;
      const next = prev.future[0];
      const newFuture = prev.future.slice(1);
      return {
        past: [...prev.past, prev.present],
        present: next,
        future: newFuture,
      };
    });
  }, []);

  const reset = useCallback((newState) => {
    setHistory({ past: [], present: newState, future: [] });
  }, []);

  // Keyboard bindings: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z (redo)
  useEffect(() => {
    const handleKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;
      // Ignore when typing in an input/textarea
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [undo, redo]);

  return {
    state: history.present,
    set,
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    reset,
  };
}

export default useUndoRedo;
